import React, { useEffect, useState } from 'react';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';
import type { GroupAggregate, LeaderboardEntry } from '../../types';

type Tab = 'dealerships' | 'signals';

const TABS: Array<{ id: Tab; label: string; icon: string }> = [
  { id: 'dealerships', label: 'Dealership Comparison', icon: 'store' },
  { id: 'signals',     label: 'Signal Breakdown',      icon: 'bar_chart' },
];

const SIGNAL_META: Array<{ key: string; label: string; weight: number }> = [
  { key: 'zoneCoverage',   label: 'Vehicle Zone Coverage', weight: 20 },
  { key: 'energy',         label: 'Energy & Enthusiasm',   weight: 15 },
  { key: 'faceOnCamera',   label: 'Salesperson On Camera', weight: 10 },
  { key: 'lighting',       label: 'Lighting Quality',      weight: 10 },
  { key: 'speechPace',     label: 'Speech Clarity & Pace', weight: 10 },
  { key: 'buyerName',      label: 'Buyer Name Mentioned',  weight: 10 },
  { key: 'cta',            label: 'Call to Action',        weight: 10 },
  { key: 'stability',      label: 'Camera Stability',      weight:  5 },
  { key: 'duration',       label: 'Video Duration',        weight:  5 },
  { key: 'featureCallout', label: 'Feature Callout Count', weight:  5 },
];

function scoreGrade(score: number): string {
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

const GRADE_COLORS: Record<string, string> = {
  A: 'text-emerald-500',
  B: 'text-primary',
  C: 'text-yellow-500',
  D: 'text-orange-500',
  F: 'text-error',
};

const GroupAdminBenchmarksPage: React.FC = () => {
  const [stats, setStats] = useState<GroupAggregate | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('dealerships');

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, lbRes] = await Promise.all([
          api.get('/group-admin/benchmarks'),
          api.get('/group-admin/benchmarks/leaderboard'),
        ]);
        setStats(statsRes.data.data);
        setLeaderboard(lbRes.data.data ?? []);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const maxVideos = Math.max(1, ...leaderboard.map((e) => e.totalVideos));
  const maxScore  = Math.max(1, ...leaderboard.map((e) => e.averageScore));

  const signalsSorted = stats
    ? [...SIGNAL_META].sort(
        (a, b) => (stats.signalAverages[a.key] ?? 0) - (stats.signalAverages[b.key] ?? 0),
      )
    : [];
  const maxSignalScore = stats
    ? Math.max(1, ...SIGNAL_META.map((s) => stats.signalAverages[s.key] ?? 0))
    : 1;

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 flex flex-col transition-colors duration-300">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
            Performance Benchmarks
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            {stats?.groupName
              ? `${stats.groupName} · dealership rankings and signal averages`
              : 'Dealership rankings and signal averages across your group'}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
          </div>
        ) : (
          <>
            {/* Summary strip */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                {[
                  { label: 'Dealerships',   value: String(stats.tenantCount) },
                  { label: 'Total Videos',  value: stats.totalVideos.toLocaleString() },
                  { label: 'Avg VQS Score', value: stats.averageScore.toFixed(1) },
                  { label: 'Flag Rate',     value: `${(stats.complianceFlagRate * 100).toFixed(1)}%` },
                  { label: 'Group Grade',   value: scoreGrade(stats.averageScore), isGrade: true },
                ].map((card) => (
                  <div
                    key={card.label}
                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm"
                  >
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2">
                      {card.label}
                    </p>
                    <p className={`text-2xl font-bold ${card.isGrade ? (GRADE_COLORS[card.value] ?? 'text-slate-900 dark:text-white') : 'text-slate-900 dark:text-white'}`}>
                      {card.value}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-1 mb-6 border-b border-slate-200 dark:border-slate-800">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 h-10 px-4 text-[10px] font-black uppercase tracking-widest transition-all border-b-2 -mb-px ${
                    tab === t.id
                      ? 'text-primary border-primary'
                      : 'text-slate-400 dark:text-slate-600 border-transparent hover:text-slate-600'
                  }`}
                >
                  <span className="material-symbols-outlined text-[16px]">{t.icon}</span>
                  {t.label}
                </button>
              ))}
            </div>

            {/* ── Dealership Comparison ─────────────────────────────── */}
            {tab === 'dealerships' && (
              <>
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8 mb-8">
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-6">
                    Dealership Comparison
                  </h2>
                  {leaderboard.length === 0 ? (
                    <p className="text-sm text-slate-400 dark:text-slate-600 py-8 text-center">
                      No data yet. Comparison populates once videos are scored.
                    </p>
                  ) : (
                    <>
                    <div className="flex items-center gap-6 mb-6 pb-4 border-b border-slate-100 dark:border-slate-800">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-primary" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Avg VQS Score</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-1.5 rounded-full bg-emerald-400" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Video Volume (relative)</span>
                      </div>
                    </div>
                    <div className="space-y-6">
                      {leaderboard.map((entry) => (
                        <div key={entry.tenantId} className="space-y-2">
                          <div className="flex items-end justify-between">
                            <div>
                              <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                                {entry.tenantName}
                              </h3>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                {entry.totalVideos} video{entry.totalVideos !== 1 ? 's' : ''}
                              </p>
                            </div>
                            <div className="flex items-center gap-6 text-right">
                              <div>
                                <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                  Avg Score
                                </div>
                                <div className="text-2xl font-bold text-primary tracking-tight">
                                  {entry.averageScore.toFixed(1)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                  Flag Rate
                                </div>
                                <div className="text-lg font-bold text-amber-500">
                                  {(entry.complianceFlagRate * 100).toFixed(1)}%
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all duration-1000"
                              style={{ width: `${(entry.averageScore / maxScore) * 100}%` }}
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-1.5 bg-slate-50 dark:bg-slate-950 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-400 rounded-full"
                                style={{ width: `${(entry.totalVideos / maxVideos) * 100}%` }}
                              />
                            </div>
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">
                              Volume
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    </>
                  )}
                </div>

                {/* Leaderboard */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest">
                      Dealership Leaderboard
                    </h2>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      By Avg Composite Score
                    </span>
                  </div>
                  {leaderboard.length === 0 ? (
                    <p className="text-sm text-slate-400 dark:text-slate-600 py-8 text-center">
                      No leaderboard data yet.
                    </p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-800">
                          {['#', 'Dealership', 'Videos', 'Avg Score', 'Grade', 'Flag Rate'].map((h) => (
                            <th
                              key={h}
                              className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-widest text-slate-400"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {leaderboard.map((entry, i) => {
                          const grade = scoreGrade(entry.averageScore);
                          return (
                            <tr
                              key={entry.tenantId}
                              className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors"
                            >
                              <td className="px-3 py-3 font-bold text-slate-900 dark:text-white text-xs">
                                {i + 1}
                              </td>
                              <td className="px-3 py-3 font-bold text-slate-900 dark:text-white text-xs">
                                {entry.tenantName}
                              </td>
                              <td className="px-3 py-3 text-xs text-slate-500 dark:text-slate-400">
                                {entry.totalVideos}
                              </td>
                              <td className="px-3 py-3 text-sm font-black text-primary">
                                {entry.averageScore.toFixed(1)}
                              </td>
                              <td className={`px-3 py-3 text-sm font-black ${GRADE_COLORS[grade] ?? ''}`}>
                                {grade}
                              </td>
                              <td className="px-3 py-3 text-xs text-amber-500">
                                {(entry.complianceFlagRate * 100).toFixed(1)}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {/* ── Signal Breakdown ──────────────────────────────────── */}
            {tab === 'signals' && stats && (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-1">
                  Signal Performance
                </h2>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">
                  Group averages across all scored videos · sorted weakest first
                </p>
                <div className="flex items-center gap-6 mb-6 pb-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-primary" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Relative to top performer</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-1.5 rounded-full bg-emerald-400" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Absolute score (out of 100)</span>
                  </div>
                </div>
                <div className="space-y-6">
                  {signalsSorted.map((sig) => {
                    const score = stats.signalAverages[sig.key] ?? 0;
                    return (
                      <div key={sig.key} className="space-y-2">
                        <div className="flex items-end justify-between">
                          <div>
                            <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                              {sig.label}
                            </h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                              Weight: {sig.weight}%
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                              Avg Score
                            </div>
                            <div className="text-2xl font-bold text-primary tracking-tight">
                              {score}
                            </div>
                          </div>
                        </div>
                        <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-1000"
                            style={{ width: `${(score / maxSignalScore) * 100}%` }}
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-1.5 bg-slate-50 dark:bg-slate-950 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-400 rounded-full"
                              style={{ width: `${score}%` }}
                            />
                          </div>
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">
                            Out of 100
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default GroupAdminBenchmarksPage;
